import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TabType } from 'components/Tabs';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Tabs } from '../../../components';
import { buildDisplayText } from '../../../utils';
import { AccountViewType, SlideOutPanelViews } from '../types';
import ManagementFees from './ManagementFees';
import CashflowDetails from './CashflowDetails';
import Transactions from './Transactions';

enum TabTypes {
  MGMT_FEES = 'mgmt_fees',
  CASH_FLOW = 'cash_flow',
  TRANSACTIONS = 'transactions',
}

enum DisplayTextKeys {
  MGMT_FEES_TAB_TITLE = 'managementFees.tab_title',
  CASH_FLOW_TAB_TITLE = 'cashFlow.tab_title',
  TRANSACTIONS_TAB_TITLE = 'transactions.tab_title',
}

interface AccountReportProps {
  openSlideout: (view: SlideOutPanelViews) => void;
  onClose: (nextView?: AccountViewType) => void;
}
const AccountReport: FC<AccountReportProps> = ({ openSlideout, onClose }) => {
  const { showCashStatements, portalTransactions: showPortalTransactions } = useFlags();

  const { t } = useTranslation();
  const displayText = useMemo(() => buildDisplayText(Object.values(DisplayTextKeys), 'account:overviewReport', t), [t]);
  const [selectedTab, setSelectedTab] = useState<string>(TabTypes.CASH_FLOW);

  const tabs = useMemo(() => {
    let options = [
      {
        id: TabTypes.CASH_FLOW,
        value: TabTypes.CASH_FLOW,
        title: <span className="text-14">{displayText[DisplayTextKeys.CASH_FLOW_TAB_TITLE]}</span>,
        content: () => <CashflowDetails />,
      },

      {
        id: TabTypes.MGMT_FEES,
        value: TabTypes.MGMT_FEES,
        title: <span className="text-14">{displayText[DisplayTextKeys.MGMT_FEES_TAB_TITLE]}</span>,
        content: () => <ManagementFees openSlideout={openSlideout} onClose={onClose} />,
      },

      // {
      //   id: TabTypes.TRANSACTIONS,
      //   value: TabTypes.TRANSACTIONS,
      //   title: <span className="text-14">{displayText[DisplayTextKeys.TRANSACTIONS_TAB_TITLE]}</span>,
      //   content: () => <Transactions />,
      // },
    ];
    options = options.filter((x) => {
      if (
        (!showCashStatements && x.id === TabTypes.CASH_FLOW) ||
        (!showPortalTransactions && x.id === TabTypes.TRANSACTIONS)
      ) {
        return false;
      }
      return true;
    });

    return options;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayText, showCashStatements, showPortalTransactions]);

  useEffect(() => {
    if (showCashStatements) {
      setSelectedTab(TabTypes.CASH_FLOW);
    } else {
      setSelectedTab(TabTypes.MGMT_FEES);
    }
  }, [showCashStatements]);

  const onTabSelect = (item: TabType) => {
    setSelectedTab(item?.value);
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-700">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500/20 to-indigo-500/10 dark:from-blue-400/20 dark:to-indigo-400/10 flex items-center justify-center">
            <svg
              className="w-5 h-5 text-blue-600 dark:text-blue-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">
              {t('account:overviewReport.report_title', 'Account Reports')}
            </h2>
            {/* <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('account:overviewReport.report_subtitle', 'View your statements, fees, and transactions')}
            </p> */}
          </div>
        </div>
      </div>
      {/* Content */}
      <div className="p-6 pt-2">
        <CashflowDetails />
      </div>
    </div>
  );
};

export default AccountReport;
